import { useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitAdverseReaction } from '../../services/adverseReactionService';

/* ── Color tokens (light theme, matches LandingPage) ── */
const C = {
  bg: '#FAFAFA',
  card: '#FFFFFF',
  accent: '#E11D48',
  accentSoft: '#FFF1F2',
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  border: '#E4E4E7',
  borderLight: '#F4F4F5',
  warnBg: '#FEF2F2',
  warnBorder: '#FECACA',
  warnText: '#991B1B',
};

const FONT_SERIF = "'Instrument Serif', Georgia, serif";
const FONT_BODY = "'DM Sans', sans-serif";

/* ── Shared styles ── */
const cardStyle: CSSProperties = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  padding: '24px',
};

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: C.accent,
  fontFamily: FONT_BODY,
  marginBottom: 6,
};

const h2Style: CSSProperties = {
  fontFamily: FONT_SERIF,
  fontSize: 'clamp(22px, 3vw, 28px)',
  fontWeight: 400,
  color: C.text,
  marginBottom: 8,
};

const bodyStyle: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.7,
  color: C.textSec,
  fontFamily: FONT_BODY,
};

/* ── FAQ Accordion ── */
function FAQ({ question, children }: { question: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...cardStyle, padding: 0 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 15,
          fontWeight: 500,
          color: C.text,
          fontFamily: FONT_BODY,
          textAlign: 'left',
        }}
        aria-expanded={open}
      >
        {question}
        <span
          style={{
            fontSize: 18,
            color: C.textMuted,
            transition: 'transform .2s',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
            flexShrink: 0,
            marginLeft: 12,
          }}
        >
          +
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 20px 16px', ...bodyStyle }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Adverse Reaction Form ── */
function AdverseReactionForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [product, setProduct] = useState('');
  const [reaction, setReaction] = useState('');
  const [onset, setOnset] = useState('');
  const [severity, setSeverity] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [consent, setConsent] = useState(false);

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    fontSize: 15,
    fontFamily: FONT_BODY,
    color: C.text,
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    outline: 'none',
  };

  const labelFieldStyle: CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: C.text,
    fontFamily: FONT_BODY,
    marginBottom: 4,
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(false);

    try {
      const result = await submitAdverseReaction({ name, email, product, reaction, onset, severity });
      if (result.success) {
        setSubmitted(true);
      } else {
        // Backend failed — fall back to mailto
        setSubmitError(true);
        openMailto();
      }
    } catch {
      // Network error — fall back to mailto
      setSubmitError(true);
      openMailto();
    } finally {
      setSubmitting(false);
    }
  }

  function openMailto() {
    const subject = encodeURIComponent(`Adverse Reaction Report — ${product || 'Unknown Product'}`);
    const body = encodeURIComponent(
      `ADVERSE REACTION REPORT\n` +
      `========================\n\n` +
      `Name: ${name}\n` +
      `Email: ${email}\n` +
      `Product(s): ${product}\n` +
      `Reaction: ${reaction}\n` +
      `Onset: ${onset}\n` +
      `Severity: ${severity}\n\n` +
      `[Submitted via hemologix.ai support page]`
    );
    window.location.href = `mailto:safety@hemologix.ai?subject=${subject}&body=${body}`;
  }

  if (submitted) {
    return (
      <div style={{ ...cardStyle, background: C.accentSoft, borderColor: C.accent, textAlign: 'center', padding: 32 }}>
        <p style={{ fontSize: 16, fontWeight: 500, color: C.text, fontFamily: FONT_BODY, marginBottom: 8 }}>
          Your report has been submitted.
        </p>
        <p style={{ ...bodyStyle, fontSize: 14 }}>
          Our safety team will review it and follow up at <strong>{email}</strong>.
          If you need immediate assistance, email{' '}
          <a href="mailto:safety@hemologix.ai" style={{ color: C.accent, textDecoration: 'underline' }}>safety@hemologix.ai</a> directly.
        </p>
        <button
          onClick={() => { setSubmitted(false); setSubmitError(false); }}
          style={{
            marginTop: 16,
            background: 'none',
            border: 'none',
            color: C.accent,
            fontSize: 14,
            fontFamily: FONT_BODY,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          File another report
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div>
          <label style={labelFieldStyle}>Your Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            style={inputStyle}
            placeholder="Full name"
          />
        </div>
        <div>
          <label style={labelFieldStyle}>Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={inputStyle}
            placeholder="your@email.com"
          />
        </div>
      </div>
      <div>
        <label style={labelFieldStyle}>Product(s) Involved</label>
        <input
          type="text"
          value={product}
          onChange={e => setProduct(e.target.value)}
          required
          style={inputStyle}
          placeholder="Which supplement(s) were you taking?"
        />
      </div>
      <div>
        <label style={labelFieldStyle}>Describe the Reaction</label>
        <textarea
          value={reaction}
          onChange={e => setReaction(e.target.value)}
          required
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
          placeholder="What symptoms did you experience?"
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div>
          <label style={labelFieldStyle}>When Did It Start?</label>
          <input
            type="text"
            value={onset}
            onChange={e => setOnset(e.target.value)}
            style={inputStyle}
            placeholder="e.g. 2 hours after first dose"
          />
        </div>
        <div>
          <label style={labelFieldStyle}>Severity</label>
          <select
            value={severity}
            onChange={e => setSeverity(e.target.value)}
            required
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">Select severity</option>
            <option value="Mild — noticeable but manageable">Mild — noticeable but manageable</option>
            <option value="Moderate — disruptive to daily activities">Moderate — disruptive to daily activities</option>
            <option value="Severe — requires medical attention">Severe — requires medical attention</option>
          </select>
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={e => setConsent(e.target.checked)}
          required
          style={{ marginTop: 3, accentColor: C.accent, flexShrink: 0 }}
        />
        <span style={{ fontSize: 13, color: C.textSec, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
          I understand this report will be reviewed by the HemoLogix safety team and consent to the
          collection of the information provided above for safety monitoring purposes.
        </span>
      </label>
      {submitError && (
        <div style={{ padding: '12px 16px', background: '#FEF2F2', border: `1px solid ${C.warnBorder}`, borderRadius: 4 }}>
          <p style={{ fontSize: 13, color: C.warnText, fontFamily: FONT_BODY }}>
            We could not submit your report electronically. Your email client should have opened with a pre-filled report.
            If it did not, please email <a href="mailto:safety@hemologix.ai" style={{ color: C.accent, textDecoration: 'underline' }}>safety@hemologix.ai</a> directly.
          </p>
        </div>
      )}
      <button
        type="submit"
        disabled={submitting || !consent}
        style={{
          alignSelf: 'flex-start',
          padding: '12px 28px',
          fontSize: 15,
          fontWeight: 600,
          fontFamily: FONT_BODY,
          color: '#FFFFFF',
          background: (submitting || !consent) ? '#A1A1AA' : C.accent,
          border: 'none',
          borderRadius: 4,
          cursor: (submitting || !consent) ? 'not-allowed' : 'pointer',
          transition: 'opacity .2s, background .2s',
        }}
        onMouseEnter={e => { if (!submitting) (e.target as HTMLButtonElement).style.opacity = '0.9'; }}
        onMouseLeave={e => { (e.target as HTMLButtonElement).style.opacity = '1'; }}
      >
        {submitting ? 'Submitting…' : 'Submit Report'}
      </button>
    </form>
  );
}

/* ── Main Support Page ── */
export function Support() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: FONT_BODY }}>
      {/* Sticky header */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(250, 250, 250, 0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${C.border}`,
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            color: C.textMuted,
            fontFamily: FONT_BODY,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16 }}>&larr;</span> Back to HemoLogix
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <a
            href="tel:+13212005433"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: C.accent,
              fontFamily: FONT_BODY,
              textDecoration: 'none',
            }}
          >
            (321) 200-5433
          </a>
          <span style={{ color: C.border }}>|</span>
          <a
            href="mailto:support@hemologix.ai"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: C.accent,
              fontFamily: FONT_BODY,
              textDecoration: 'none',
            }}
          >
            support@hemologix.ai
          </a>
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '48px 20px 80px' }}>
        {/* Hero */}
        <section style={{ marginBottom: 48 }}>
          <p style={labelStyle}>Support</p>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(32px, 5vw, 44px)',
              fontWeight: 400,
              color: C.text,
              lineHeight: 1.15,
              marginBottom: 12,
            }}
          >
            Need Help?
          </h1>
          <p style={{ ...bodyStyle, maxWidth: 540 }}>
            Real people, real answers. Whether you have a question about your results, your order,
            or need to report a concern — we are here.
          </p>
        </section>

        {/* Contact Info */}
        <section style={{ marginBottom: 40 }}>
          <div style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24 }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Phone</p>
                <a
                  href="tel:+13212005433"
                  style={{ fontSize: 15, color: C.accent, textDecoration: 'none', fontFamily: FONT_BODY }}
                >
                  (321) 200-5433
                </a>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Email</p>
                <a
                  href="mailto:support@hemologix.ai"
                  style={{ fontSize: 15, color: C.accent, textDecoration: 'none', fontFamily: FONT_BODY }}
                >
                  support@hemologix.ai
                </a>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Hours</p>
                <p style={{ fontSize: 15, color: C.textSec, fontFamily: FONT_BODY }}>
                  Mon – Fri, 9 AM – 5 PM ET
                </p>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Response Time</p>
                <p style={{ fontSize: 15, color: C.textSec, fontFamily: FONT_BODY }}>
                  Within 24 hours on business days
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Help Path */}
        <section style={{ marginBottom: 48 }}>
          <p style={labelStyle}>Getting Help</p>
          <h2 style={h2Style}>Choose Your Path</h2>
          <p style={{ ...bodyStyle, marginBottom: 20 }}>
            Find the fastest route to an answer.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              {
                title: 'Lab Results',
                desc: 'Questions about your biomarker report or what your results mean.',
                action: 'Email us',
                href: 'mailto:support@hemologix.ai?subject=Question About My Lab Results',
              },
              {
                title: 'Orders & Shipping',
                desc: 'Track an order, request a return, or resolve a shipping issue.',
                action: 'Track order',
                href: '/order/lookup',
                isRoute: true,
              },
              {
                title: 'Account Help',
                desc: 'Trouble logging in, recovering a session, or updating information.',
                action: 'Email us',
                href: 'mailto:support@hemologix.ai?subject=Account Help Request',
              },
              {
                title: 'Adverse Reaction',
                desc: 'Report a reaction to a supplement. Discontinue use first.',
                action: 'File a report',
                href: '#adverse-reaction',
                isAnchor: true,
              },
            ].map((item) => (
              <div key={item.title} style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY }}>
                  {item.title}
                </p>
                <p style={{ fontSize: 14, color: C.textSec, fontFamily: FONT_BODY, lineHeight: 1.6, flex: 1 }}>
                  {item.desc}
                </p>
                {'isRoute' in item ? (
                  <button
                    onClick={() => navigate(item.href)}
                    style={{
                      alignSelf: 'flex-start',
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.accent,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: FONT_BODY,
                      padding: 0,
                    }}
                  >
                    {item.action} &rarr;
                  </button>
                ) : 'isAnchor' in item ? (
                  <a
                    href={item.href}
                    style={{
                      alignSelf: 'flex-start',
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.accent,
                      textDecoration: 'none',
                      fontFamily: FONT_BODY,
                    }}
                  >
                    {item.action} &rarr;
                  </a>
                ) : (
                  <a
                    href={item.href}
                    style={{
                      alignSelf: 'flex-start',
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.accent,
                      textDecoration: 'none',
                      fontFamily: FONT_BODY,
                    }}
                  >
                    {item.action} &rarr;
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Common Questions */}
        <section style={{ marginBottom: 48 }}>
          <p style={labelStyle}>FAQ</p>
          <h2 style={h2Style}>Common Questions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
            <FAQ question="How do I get my lab results analyzed?">
              Upload your lab results from the{' '}
              <a href="/" style={{ color: C.accent }}>home page</a>.
              Our free analysis compares your biomarkers against functional medicine
              optimal ranges — no account or credit card required.
            </FAQ>
            <FAQ question="I placed an order. Where is it?">
              Check your order status on the{' '}
              <a href="/order/lookup" style={{ color: C.accent }}>order lookup page</a>.
              If you need further help, email us with your order confirmation number.
            </FAQ>
            <FAQ question="What is your refund policy?">
              Unopened supplements may be returned within 30 days of delivery for a full refund.
              Opened products are eligible only if defective. See our{' '}
              <a href="/terms#purchases" style={{ color: C.accent }}>Terms of Service</a> for
              full details, or email us to start a return.
            </FAQ>
            <FAQ question="Is my data secure?">
              Yes. We follow HIPAA-compliant data handling practices. Your lab data is encrypted
              in transit and at rest. Read our{' '}
              <a href="/privacy" style={{ color: C.accent }}>Privacy Policy</a> for details.
            </FAQ>
          </div>
        </section>

        {/* Adverse Reaction Report */}
        <section id="adverse-reaction" style={{ marginBottom: 48, scrollMarginTop: 80 }}>
          <div
            style={{
              background: C.warnBg,
              border: `1px solid ${C.warnBorder}`,
              borderRadius: 4,
              padding: '28px 24px',
            }}
          >
            <p style={{ ...labelStyle, color: C.warnText }}>Safety</p>
            <h2 style={{ ...h2Style, fontSize: 'clamp(20px, 3vw, 24px)', marginBottom: 12 }}>
              Report an Adverse Reaction
            </h2>
            <div
              style={{
                ...bodyStyle,
                fontSize: 14,
                marginBottom: 20,
                padding: '12px 16px',
                background: '#FFFFFF',
                borderRadius: 4,
                border: `1px solid ${C.warnBorder}`,
              }}
            >
              <strong style={{ color: C.warnText }}>If you are experiencing a medical emergency, call 911.</strong>
              <br />
              If you experience any adverse reaction to a supplement, <strong style={{ color: C.text }}>discontinue use immediately</strong> and
              contact your healthcare provider. Then file a report below so we can update your profile and investigate.
              <br /><br />
              You may also file a voluntary report with the FDA:{' '}
              <a
                href="https://www.safetyreporting.hhs.gov"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.accent, textDecoration: 'underline' }}
              >
                FDA MedWatch Safety Reporting Portal
              </a>
            </div>
            <AdverseReactionForm />
          </div>
        </section>

        {/* Footer */}
        <footer
          style={{
            borderTop: `1px solid ${C.border}`,
            paddingTop: 24,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 20,
            fontSize: 13,
            color: C.textMuted,
            fontFamily: FONT_BODY,
          }}
        >
          <a href="/privacy" style={{ color: C.textMuted, textDecoration: 'none' }}>Privacy Policy</a>
          <a href="/terms" style={{ color: C.textMuted, textDecoration: 'none' }}>Terms of Service</a>
          <a href="/refund-policy" style={{ color: C.textMuted, textDecoration: 'none' }}>Refund Policy</a>
          <a href="mailto:support@hemologix.ai" style={{ color: C.textMuted, textDecoration: 'none' }}>support@hemologix.ai</a>
        </footer>
      </main>
    </div>
  );
}
